import { useCallback, useEffect, useRef } from "react"
import soundsConfig from "../data/sounds-config.json"

// Types
interface PreloadedAudio {
  audio: HTMLAudioElement
  lastPlayTime: number
}

// Global state
const preloadedAudio = new Map<string, PreloadedAudio>()
let audioContext: AudioContext | null = null
let audioContextInitialized = false
let isGloballyMuted = false
let userInteractionDetected = false

// Helper to build full URL from config
function getSoundUrl(path: string): string {
  if (path.startsWith("http")) return path
  const basePath = path.startsWith("/") ? path.slice(1) : path
  return `${soundsConfig.baseUrl}/${basePath}`
}

// Helper to get sound paths from config
export function getSoundPath(category: string, subcategory?: string, key?: string): string | string[] | null {
  const cats = soundsConfig.categories as Record<string, unknown>
  if (!cats[category]) return null

  const cat = cats[category] as Record<string, unknown>
  if (!subcategory) return cat as unknown as string | string[]

  const subcat = cat[subcategory] as Record<string, unknown> | string | string[]
  if (!key || typeof subcat === "string" || Array.isArray(subcat)) return subcat as string | string[]

  return (subcat as Record<string, unknown>)[key] as string | string[] | null
}

// Audio context initialization
function initializeAudioContext() {
  if (audioContextInitialized) return

  try {
    const AudioCtx =
      window.AudioContext || (window as typeof window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext
    audioContext = new AudioCtx()

    if (audioContext.state === "suspended") {
      audioContext.resume()
    }

    audioContextInitialized = true
  } catch (error) {
    console.warn("Audio context initialization failed:", error)
  }
}

// Preload and cache audio elements
function preloadAudio(url: string): HTMLAudioElement {
  const existing = preloadedAudio.get(url)
  if (existing) {
    return existing.audio
  }

  const audio = new Audio()
  audio.preload = "auto"
  audio.src = url
  audio.load()
  preloadedAudio.set(url, { audio, lastPlayTime: 0 })
  return audio
}

// Play audio with optimizations
export function playOptimizedAudio(src: string, volume = 1, allowOverlay = false, bypassMute = false) {
  if (typeof window === "undefined") return

  // Check global mute state
  if (!bypassMute && isGloballyMuted) {
    return
  }

  // Initialize audio context on first play
  if (!audioContextInitialized) {
    initializeAudioContext()
  }

  // Resume audio context if suspended
  if (audioContext && audioContext.state === "suspended") {
    audioContext.resume()
  }

  // Handle active audio
  if (!allowOverlay && window.activeCallAudio) {
    window.activeCallAudio.pause()
    window.activeCallAudio = undefined
  }

  // Use preloaded audio element
  const audio = preloadAudio(src)
  audio.currentTime = 0
  audio.volume = volume

  if (!allowOverlay) {
    window.activeCallAudio = audio
  }

  // Play with error handling
  const playPromise = audio.play()
  if (playPromise !== undefined) {
    playPromise.catch((error: unknown) => {
      console.warn("Audio play failed:", error)
      // Fallback with delay
      setTimeout(() => {
        audio.play().catch(() => {
          console.warn("Audio fallback play also failed")
        })
      }, 100)
    })
  }

  if (!allowOverlay) {
    const handleEnded = () => {
      if (window.activeCallAudio === audio) {
        window.activeCallAudio = undefined
      }
      audio.removeEventListener("ended", handleEnded)
    }
    audio.addEventListener("ended", handleEnded)
  }
}

// Global mute control
export function setGlobalMute(muted: boolean) {
  isGloballyMuted = muted
}

function getGlobalMute(): boolean {
  return isGloballyMuted
}

// Setup user interaction detection for audio
function setupAudioInitialization() {
  if (typeof window === "undefined") return

  const initOnInteraction = () => {
    if (userInteractionDetected) return
    userInteractionDetected = true

    setTimeout(() => {
      initializeAudioContext()
    }, 50)
  }

  const events = ["touchstart", "touchend", "mousedown", "keydown", "click"]
  for (const event of events) {
    document.addEventListener(event, initOnInteraction, { once: true, passive: true })
  }
}

// Initialize on load
if (typeof window !== "undefined") {
  setupAudioInitialization()
}

// Declare global audio property
declare global {
  interface Window {
    activeCallAudio?: HTMLAudioElement | undefined
  }
}

// Custom hook for sound management
export function useSoundManager() {
  const lastPlayTimesRef = useRef<Map<string, number>>(new Map())

  useEffect(() => {
    // Preload critical sounds on mount
    const criticalSounds = [
      getSoundPath("bracket", "select", "clicks"),
      getSoundPath("bracket", "select", "error"),
      getSoundPath("bracket", "select", "deselect"),
      getSoundPath("bracket", "card", "open"),
      getSoundPath("bracket", "card", "close"),
    ]
      .flat()
      .filter(Boolean) as string[]

    for (const path of criticalSounds) {
      if (typeof path === "string") {
        preloadAudio(getSoundUrl(path))
      }
    }
  }, [])

  const playSound = useCallback(
    (
      category: string,
      subcategory?: string,
      key?: string,
      options: { volume?: number; allowOverlay?: boolean; bypassMute?: boolean; index?: number } = {},
    ) => {
      const soundPath = getSoundPath(category, subcategory, key)
      if (!soundPath) return

      let url: string
      if (Array.isArray(soundPath)) {
        const idx = options.index ?? Math.floor(Math.random() * soundPath.length)
        const pathItem = soundPath[Math.min(idx, soundPath.length - 1)]
        if (!pathItem) return
        url = getSoundUrl(pathItem)
      } else {
        url = getSoundUrl(soundPath)
      }

      // Debounce check
      const now = Date.now()
      const lastPlayTime = lastPlayTimesRef.current.get(url) || 0
      if (now - lastPlayTime < 1) {
        return
      }
      lastPlayTimesRef.current.set(url, now)

      playOptimizedAudio(url, options.volume ?? 1, options.allowOverlay ?? false, options.bypassMute ?? false)
    },
    [],
  )

  return {
    playSound,
    setGlobalMute,
    getGlobalMute,
    preloadAudio: (category: string, subcategory?: string, key?: string) => {
      const soundPath = getSoundPath(category, subcategory, key)
      if (!soundPath) return

      if (Array.isArray(soundPath)) {
        for (const path of soundPath) {
          preloadAudio(getSoundUrl(path))
        }
      } else {
        preloadAudio(getSoundUrl(soundPath))
      }
    },
  }
}
